import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Activity,
  Wifi,
  WifiOff,
  Bell,
  BellOff,
  Server,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Lock,
  RefreshCw,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusDot = ({ ok, label, testId }) => (
  <div className="flex items-center gap-2" data-testid={testId}>
    {ok ? (
      <CheckCircle2 className="w-4 h-4 text-green-400" />
    ) : (
      <XCircle className="w-4 h-4 text-red-400" />
    )}
    <span className="font-mono text-xs text-white">{label}</span>
  </div>
);

const Row = ({ label, value, mono = true, testId }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-white/5" data-testid={testId}>
    <span className="font-mono text-[11px] text-muted-foreground uppercase">{label}</span>
    <span
      className={`${mono ? 'font-mono' : 'font-inter'} text-xs text-white text-right break-all`}
    >
      {value}
    </span>
  </div>
);

const DiagnosticsPage = () => {
  const { user, wsConnected, onlineUsers, alerts, hasPermission } = useAuth();
  const canView = hasPermission('can_manage_team') || user?.role === 'security_head';

  const [pushInfo, setPushInfo] = useState(null);
  const [notifPerm, setNotifPerm] = useState('default');
  const [swStatus, setSwStatus] = useState('unknown');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const refreshAll = async () => {
    setLoading(true);
    await Promise.all([loadPush(), loadServiceWorker()]);
    if ('Notification' in window) setNotifPerm(Notification.permission);
    setLoading(false);
  };

  const loadPush = async () => {
    try {
      const res = await axios.get(`${API_URL}/push/status`);
      setPushInfo(res.data);
    } catch (e) {
      setPushInfo({ error: e.message });
    }
  };

  const loadServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) {
      setSwStatus('unsupported');
      return;
    }
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) {
        setSwStatus('not-registered');
        return;
      }
      setSwStatus(reg.active ? 'active' : reg.installing ? 'installing' : 'waiting');
      const sub = await reg.pushManager.getSubscription();
      setSubscription(sub);
    } catch (e) {
      setSwStatus(`error: ${e.message}`);
    }
  };

  const sendTestPush = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API_URL}/push/test-me`);
      if (res.data.success) {
        toast.success(`Push sent to ${res.data.delivered} of your device${res.data.devices === 1 ? '' : 's'} — check your phone now`);
      } else {
        toast.error(res.data.error || 'Could not send push');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test push');
    } finally {
      setTesting(false);
    }
  };

  const testInAppVibration = () => {
    if (!('vibrate' in navigator)) {
      toast.error('This device does not support navigator.vibrate (iOS Safari has no support).');
      return;
    }
    // Same violent pattern used by the GlobalAlertOverlay for emergency alerts
    const pattern = [1200, 30, 800, 30, 1200, 30, 800, 30, 1500, 30, 600, 30, 1200, 30];
    try {
      const ok = navigator.vibrate(pattern);
      if (ok) {
        toast.success('Buzzing now — feel for a ~7 second sustained vibration');
      } else {
        toast.error('Browser blocked the vibration. Tap anywhere on the page first then retry.');
      }
    } catch (err) {
      toast.error('Vibration failed: ' + err.message);
    }
  };

  if (!canView) {
    return (
      <div className="p-4 md:p-6" data-testid="diagnostics-no-access">
        <PageHeader title="Diagnostics" subtitle="Access restricted" />
        <Card className="bg-panel border-white/10 rounded-sm mt-6">
          <CardContent className="p-12 text-center space-y-4">
            <Lock className="w-12 h-12 text-red-400 mx-auto" />
            <p className="font-chivo font-bold text-lg text-white">Admin Only</p>
            <p className="font-inter text-sm text-muted-foreground">
              Diagnostics is available to Security Head / Admin roles.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const activeAlerts = alerts.filter((a) => a.status === 'active').length;

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="diagnostics-page">
      <div className="flex items-center justify-between gap-4">
        <PageHeader title="Diagnostics" subtitle="Real-time connection & subsystem health" />
        <Button
          variant="outline"
          onClick={refreshAll}
          disabled={loading}
          className="border-white/20 rounded-sm font-mono text-xs uppercase"
          data-testid="diag-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* WebSocket + Realtime */}
      <Card className="bg-panel border-white/10 rounded-sm">
        <CardHeader className="border-b border-white/10 pb-4">
          <CardTitle className="font-chivo font-bold text-lg uppercase tracking-tight text-white flex items-center gap-2">
            {wsConnected ? (
              <Wifi className="w-5 h-5 text-green-400" />
            ) : (
              <WifiOff className="w-5 h-5 text-red-400" />
            )}
            Realtime (WebSocket)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-1">
          <Row
            label="WebSocket"
            value={
              <Badge
                variant="outline"
                className={`font-mono text-[10px] ${
                  wsConnected
                    ? 'border-green-500/50 text-green-400'
                    : 'border-red-500/50 text-red-400'
                }`}
                data-testid="diag-ws-status"
              >
                {wsConnected ? 'CONNECTED' : 'DISCONNECTED'}
              </Badge>
            }
            mono={false}
          />
          <Row label="Online Users" value={onlineUsers.length} testId="diag-online-count" />
          <Row label="Active Alerts" value={activeAlerts} testId="diag-active-alerts" />
          <Row label="Your User ID" value={user?.id || '—'} />
          <Row label="Your Role" value={user?.role?.replace(/_/g, ' ') || '—'} />
        </CardContent>
      </Card>

      {/* Push Notifications */}
      <Card className="bg-panel border-white/10 rounded-sm">
        <CardHeader className="border-b border-white/10 pb-4">
          <CardTitle className="font-chivo font-bold text-lg uppercase tracking-tight text-white flex items-center gap-2">
            {notifPerm === 'granted' ? (
              <Bell className="w-5 h-5 text-green-400" />
            ) : (
              <BellOff className="w-5 h-5 text-red-400" />
            )}
            Push Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-1">
          <Row
            label="Browser Permission"
            value={
              <Badge
                variant="outline"
                className={`font-mono text-[10px] ${
                  notifPerm === 'granted'
                    ? 'border-green-500/50 text-green-400'
                    : notifPerm === 'denied'
                    ? 'border-red-500/50 text-red-400'
                    : 'border-yellow-500/50 text-yellow-400'
                }`}
                data-testid="diag-notif-perm"
              >
                {notifPerm.toUpperCase()}
              </Badge>
            }
            mono={false}
          />
          <Row label="Service Worker" value={swStatus} testId="diag-sw-status" />
          <Row
            label="Browser Subscription"
            value={subscription ? 'ACTIVE' : 'NOT SUBSCRIBED'}
            testId="diag-sub-status"
          />
          {pushInfo && !pushInfo.error && (
            <>
              <Row
                label="Server Subscription"
                value={pushInfo.subscribed ? 'REGISTERED' : 'NOT REGISTERED'}
              />
              <Row label="Total Subscribers" value={pushInfo.total_subscriptions ?? '—'} />
            </>
          )}
          {pushInfo?.error && (
            <div className="flex items-center gap-2 text-red-400 py-2">
              <AlertCircle className="w-4 h-4" />
              <span className="font-mono text-xs">{pushInfo.error}</span>
            </div>
          )}

          <div className="pt-4 space-y-2">
            <Button
              onClick={testInAppVibration}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold uppercase rounded-sm"
              data-testid="diag-test-in-app-vibration-btn"
            >
              ⚡ Test In-App Vibration (this device)
            </Button>
            <p className="font-mono text-[10px] text-muted-foreground text-center">
              Fires the violent pattern via navigator.vibrate while the app is open. iOS Safari does not support this API.
            </p>

            <Button
              onClick={sendTestPush}
              disabled={testing || !subscription}
              className="w-full bg-primary hover:bg-primary/90 font-bold uppercase rounded-sm"
              data-testid="diag-send-test-push-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {testing ? 'Sending...' : 'Send Test Push to My Phone (lock-screen)'}
            </Button>
            <p className="font-mono text-[10px] text-muted-foreground text-center">
              Lock your phone, hit this from your laptop. Confirms violent vibration works through the OS notification channel even when the app is closed.
            </p>
            {!subscription && (
              <p className="font-mono text-[10px] text-yellow-400 text-center mt-1">
                Subscribe to notifications first (Settings → Notifications) on the phone you want to test.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Info */}
      <Card className="bg-panel border-white/10 rounded-sm">
        <CardHeader className="border-b border-white/10 pb-4">
          <CardTitle className="font-chivo font-bold text-lg uppercase tracking-tight text-white flex items-center gap-2">
            <Server className="w-5 h-5 text-primary" />
            Environment
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-1">
          <Row
            label="Backend URL"
            value={process.env.REACT_APP_BACKEND_URL}
            testId="diag-backend-url"
          />
          <Row label="User Agent" value={navigator.userAgent.slice(0, 80) + '...'} />
          <Row label="Platform" value={navigator.platform} />
          <Row label="Online" value={navigator.onLine ? 'YES' : 'NO'} />
          <Row
            label="Activity"
            value={
              <span className="inline-flex items-center gap-1">
                <Activity className="w-3 h-3 text-primary animate-pulse" />
                LIVE
              </span>
            }
            mono={false}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default DiagnosticsPage;
